import { useAssetHub } from '@/managers'
import { useState } from 'react'
import {
	Button,
	type ComplexNumericMode,
	MiniTabbedWidgetWrapper,
	WidgetInputComplexNumericField,
	WidgetInputTextField,
	WidgetLabel,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import { logger } from '../../../../utilities'
import type { ImageContentMode, ImageRefTarget, ImageWidthMode, Layer } from '../../data/v1'
import { BoxTabIcon, ContentTabIcon } from '../../glyphs/tabs'
import { useSizeSetByParent } from '../../lib/parent-sizing'
import { useDesignerActions } from '../../use-designer-actions'
import { BoxInspector } from './box'
import { FloatControls } from './float-controls'
import { SetByParent } from './set-by-parent'

const contentModeItems: WidgetSelectGroupItem<ImageContentMode>[] = [
	{ id: 'ref', label: 'Reference', render: () => <span className="text-xs">Ref</span> },
	{ id: 'custom', label: 'Custom', render: () => <span className="text-xs">Custom</span> },
]

const refTargetItems: WidgetSelectGroupItem<ImageRefTarget>[] = [
	{ id: 'logo', label: 'Logo', render: () => <span className="text-xs">Logo</span> },
	{ id: 'platformLogo', label: 'Platform Logo', render: () => <span className="text-xs">Platform</span> },
]

const widthModes: ComplexNumericMode<ImageWidthMode>[] = [
	{ id: 'auto', label: 'Auto', numeric: false },
	{ id: 'px', label: 'px', numeric: true },
	{ id: '%', label: '%', numeric: true },
]

export type ImageLayerInspectorProps = {
	layer: Extract<Layer, { type: 'image' }>
}

export function ImageLayerInspector({ layer }: ImageLayerInspectorProps) {
	const { updateLayer } = useDesignerActions()
	const assetHub = useAssetHub()
	const [tab, setTab] = useState<'content' | 'style'>('content')
	const sizeSetByParent = useSizeSetByParent(layer.id)

	const widthValue = layer.width.mode === '%'
		? layer.width.percentValue
		: layer.width.pxValue

	const handlePickFromAssets = async () => {
		try {
			const asset = await assetHub.open()
			if (asset) {
				updateLayer(layer.id, { customValue: asset.url })
			}
		} catch (error) {
			// The picker rejects with an AbortError when re-opened by another
			// caller — that's expected; anything else is a real failure worth
			// recording.
			if (error instanceof DOMException && error.name === 'AbortError') {
				return
			}
			logger.error(error, {
				tags: { component: 'ImageLayerInspector', action: 'open-asset-hub' },
				data: { layerId: layer.id },
			})
		}
	}

	return (
		<>
			<FloatControls layer={layer} />
			<MiniTabbedWidgetWrapper
				value={tab}
				onChange={setTab}
				tabs={[
					{
						id: 'content',
						icon: <ContentTabIcon />,
						label: 'Content',
						children: (
							<div className="flex flex-col gap-2">
								<WidgetLabel label="Source">
									<WidgetSelectGroup
										value={layer.content}
										onChange={(value) => updateLayer(layer.id, { content: value })}
										items={contentModeItems}
									/>
								</WidgetLabel>

								{layer.content === 'ref'
									? (
										<WidgetLabel label="Reference">
											<WidgetSelectGroup
												value={layer.refTo}
												onChange={(value) => updateLayer(layer.id, { refTo: value })}
												items={refTargetItems}
											/>
										</WidgetLabel>
									)
									: (
										<>
											<WidgetLabel label="URL">
												<WidgetInputTextField
													value={layer.customValue}
													onChange={(value) => updateLayer(layer.id, { customValue: value })}
													placeholder="Enter image URL..."
												/>
											</WidgetLabel>
											<Button size="short" type="committal" onClick={handlePickFromAssets}>
												Pick from assets
											</Button>
										</>
									)}

								<SetByParent isSet={sizeSetByParent.width}>
									<WidgetLabel label="Width">
										<WidgetInputComplexNumericField
											value={widthValue}
											onChange={(value) => {
												const key = layer.width.mode === '%' ? 'percentValue' : 'pxValue'
												updateLayer(layer.id, { width: { ...layer.width, [key]: value } })
											}}
											min={0}
											defaultValue={100}
											modes={widthModes}
											mode={layer.width.mode}
											onModeChange={(mode) =>
												updateLayer(layer.id, { width: { ...layer.width, mode } })}
										/>
									</WidgetLabel>
								</SetByParent>
							</div>
						),
					},
					{
						id: 'style',
						icon: <BoxTabIcon />,
						label: 'Box',
						children: (
							<BoxInspector
								layerId={layer.id}
								opacity={layer.opacity}
								onOpacityChange={(opacity) => updateLayer(layer.id, { opacity })}
								onTransientOpacityChange={(opacity) =>
									updateLayer(layer.id, { opacity }, { transient: true })}
								background={layer.background}
								onBackgroundChange={(updates, options) =>
									updateLayer(layer.id, (prev) => ({
										background: { ...(prev as typeof layer).background, ...updates },
									}), options)}
								borderRadius={layer.borderRadius}
								onBorderRadiusChange={(borderRadius) => updateLayer(layer.id, { borderRadius })}
								borderRadiusCorners={layer.borderRadiusCorners}
								onBorderRadiusCornersChange={(borderRadiusCorners) =>
									updateLayer(layer.id, { borderRadiusCorners })}
								spacing={layer.spacing}
								onSpacingChange={(spacing) => updateLayer(layer.id, { spacing })}
								onTransientSpacingChange={(spacing) =>
									updateLayer(layer.id, { spacing }, { transient: true })}
							/>
						),
					},
				]}
			/>
		</>
	)
}
